import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, ArrowRight, Shield } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Alert from '../components/common/Alert';
import KeyboardDismissView from '../components/common/KeyboardDismissView';
import { useFormNavigation } from '../hooks/useFormNavigation';

export default function SetPasswordPage() {
  const navigate = useNavigate();
  const { user, setPassword, error, clearError } = useAuth();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState('');

  const { registerField, focusNext } = useFormNavigation({
    onSubmit: () => document.getElementById('setpassword-submit')?.click()
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setValidationError('');
  };

  const validate = () => {
    if (formData.password.length < 8) {
      setValidationError('Password must be at least 8 characters');
      return false;
    }
    if (!/[A-Z]/.test(formData.password)) {
      setValidationError('Password must contain at least one uppercase letter');
      return false;
    }
    if (!/[a-z]/.test(formData.password)) {
      setValidationError('Password must contain at least one lowercase letter');
      return false;
    }
    if (!/[0-9]/.test(formData.password)) {
      setValidationError('Password must contain at least one number');
      return false;
    }
    if (!/[^A-Za-z0-9]/.test(formData.password)) {
      setValidationError('Password must contain at least one special character');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setValidationError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);

    try {
      await setPassword(formData.password, formData.confirmPassword);
      navigate('/');
    } catch {
      // Error is handled by context
    } finally {
      setLoading(false);
    }
  };

  const displayError = validationError || error;

  return (
    <div className="min-h-[100dvh] bg-surface-50 flex">
      {/* Left Panel - Branding */}
      <div
        className="hidden xl:flex lg:w-1/2 relative overflow-hidden"
        style={{ backgroundColor: 'var(--sidebar-bg)' }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-income-500 rounded-full blur-3xl" />
          <div
            className="absolute bottom-20 right-20 w-96 h-96 rounded-full blur-3xl"
            style={{ backgroundColor: 'var(--accent-bg)' }}
          />
        </div>

        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-20">
          {/* Logo */}
          <div className="mb-8">
            <img
              src="/logo.png"
              alt="Hisab Journey"
              className="w-14 h-14 mb-6 rounded-full"
            />
            <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight">
              Secure your<br />account
            </h1>
            <p className="text-surface-400 text-lg mt-4 max-w-md">
              Set a password to add an extra layer of security to your account.
            </p>
          </div>

          {/* Features */}
          <div className="space-y-4 mt-8">
            {[
              'Login with email & password anytime',
              'Extra security for your data',
              'Required for account recovery',
              'Access from any device'
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-3 text-surface-300">
                <div className="w-5 h-5 rounded-full bg-income-500/20 flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-income-500" />
                </div>
                {feature}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Set Password Form */}
      <KeyboardDismissView className="flex-1 flex items-center justify-center p-6 sm:p-12 overflow-y-auto safe-area-top">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="xl:hidden text-center mb-8">
            <img
              src="/logo.png"
              alt="Hisab Journey"
              className="w-16 h-16 mx-auto mb-3 rounded-2xl"
            />
            <h1 className="text-xl font-bold text-surface-900">Hisab Journey</h1>
          </div>

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-income-100 flex items-center justify-center">
                <Shield className="w-6 h-6 text-income-600" />
              </div>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-surface-900">
              Set your password
            </h2>
            <p className="text-surface-500 mt-2">
              {user?.name ? `Welcome, ${user.name}!` : 'Welcome!'} Create a secure password to protect your account.
            </p>
          </div>

          {/* Info Box */}
          <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-100">
            <p className="text-sm text-blue-800">
              Setting a password allows you to log in with your email address in addition to Google Sign-In.
            </p>
          </div>

          {displayError && (
            <Alert
              variant="error"
              message={displayError}
              onClose={() => {
                clearError();
                setValidationError('');
              }}
            />
          )}

          <form onSubmit={handleSubmit} className="space-y-5 mt-6">
            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
                <input
                  ref={(el) => registerField('password', el)}
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a strong password"
                  enterKeyHint="next"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), focusNext('password'))}
                  className="w-full pl-12 pr-12 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-surface-900 focus:border-transparent transition-all"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-surface-400 hover:text-surface-600 transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              <p className="text-xs text-surface-500 mt-2">
                Min 8 characters, uppercase, lowercase, number & special character
              </p>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-2">
                Confirm password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
                <input
                  ref={(el) => registerField('confirmPassword', el)}
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Re-enter your password"
                  enterKeyHint="done"
                  className="w-full pl-12 pr-4 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-surface-900 focus:border-transparent transition-all"
                  required
                />
              </div>
            </div>

            {/* Submit */}
            <button
              id="setpassword-submit"
              type="submit"
              className="w-full py-3.5 rounded-xl font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed mt-6"
              style={{
                backgroundColor: 'var(--accent-bg)',
                color: 'var(--accent-text)'
              }}
              disabled={loading}
            >
              {loading ? (
                'Setting password...'
              ) : (
                <>
                  Set password & continue
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>
        </div>
      </KeyboardDismissView>
    </div>
  );
}
